import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import PermissionConstants, {PermissionLevel} from 'auth/PermissionConstants';
import {EventConstants} from 'constants/EventConstants';
import {NavStoreRegistry} from 'constants/RegistryConstants';
import * as SourceConstants from 'constants/SourceConstants';
import {NavStoreProject, NavStoreSource} from 'constants/SourceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';
import {GroupResult} from 'search/SearchConstants';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';

const {PROJECT_TYPE, REGISTRY_TYPE} = SourceConstants;

class NavStore extends Store {
  sources!: Array<NavStoreSource> | null;

  resetState(): void {
    this.sources = null;
  }

  getSources(): Array<NavStoreSource> | null {
    return this.sources;
  }

  getSource(sourceId: string): NavStoreSource | null {
    if (!this.sources) {
      return null;
    } else {
      return _.findWhere(this.sources, {id: sourceId}) || null;
    }
  }

  getRegistries(): Array<NavStoreRegistry> {
    if (this.sources) {
      return this.sources.filter(
        (source) => source.sourceType === REGISTRY_TYPE && !isArchived(source)
      ) as Array<NavStoreRegistry>;
    }
    return [];
  }

  getProjects(): Array<NavStoreProject> {
    if (this.sources) {
      return this.sources.filter((source) => source.sourceType === PROJECT_TYPE) as Array<NavStoreProject>;
    }
    return [];
  }

  isLoading(): boolean {
    return !this.sources;
  }

  hasSourceAuth(sourceId: string, permissionLevel: PermissionLevel): boolean {
    const source = this.getSource(sourceId);
    if (!source) {
      return false;
    }
    const sourcePermissions = source.permissions;
    switch (permissionLevel) {
      case PermissionConstants.READ:
        return sourcePermissions.readable;
      case PermissionConstants.APPEND:
        return sourcePermissions.appendable;
      case PermissionConstants.WRITE:
        return sourcePermissions.writable;
      case PermissionConstants.ADMIN:
        return sourcePermissions.admin;
      default:
        throw new Error(`Unexpected permission level: ${permissionLevel}`);
    }
  }

  isSourceWritable(sourceId: string): boolean {
    return this.hasSourceAuth(sourceId, PermissionConstants.WRITE);
  }

  /**
   *
   * @param searchResultGroups The search result groups to extract the child ids from.
   * @returns {Array<string>} The ids of all the children of all the search result groups.
   */
  _getChildIds(searchResultGroups: Array<GroupResult>): Array<string> {
    return searchResultGroups.flatMap((group) => group.childIds);
  }

  /**
   * Invalidate listings for `itemIds`. That is, refresh them so they are no longer stale.
   */
  _invalidateListedItems(itemIds: Array<string>): void {
    const invalidatedSearchIds = Object.entries(
      getStore().getState().models.managed[ManagedModelType.SEARCH_RESULTS] || {}
    )
      .filter(
        ([searchId, searchResults]) =>
          _.intersection(
            searchResults ? [...searchResults.ids, ...this._getChildIds(searchResults.groups ?? [])] : [],
            itemIds
          ).length
      )
      .map(([searchId, searchResults]) => searchId);
    const invalidatedSearchSessionIds = Object.entries(getStore().getState().ui.search.stateBySessionId)
      .filter(([searchSessionId, sessionState]) => invalidatedSearchIds.includes(sessionState.searchId))
      .map(([searchSessionId, sessionState]) => searchSessionId);
    _.defer(() => {
      for (const searchSessionId of invalidatedSearchSessionIds) {
        getStore().dispatch(SearchUIReduxActions.refreshListing(searchSessionId));
      }
      // clear expandedIds for invalidated searchIds to avoid having to close first and then expand the group
      // in order to view the updated child items
      for (const searchId of invalidatedSearchIds) {
        getStore().dispatch({
          type: SearchUIReduxActions.SearchUIActionType.EXPANDED_IDS_UPDATED,
          searchId,
          expandedIds: [],
        });
      }
    });
  }

  _updateSource(sourceId: string, sourceUpdates: Partial<NavStoreSource>): void {
    // eslint-disable-next-line no-restricted-syntax
    const sourceIndex = _.findIndex(this.sources!, (source) => source.id === sourceId);
    const updatedSource = reactUpdate(this.sources![sourceIndex], {$merge: sourceUpdates});
    this.sources = reactUpdate(this.sources, {$splice: [[sourceIndex, 1, updatedSource]]});
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.BULK_ARCHIVE_REGISTRIES_COMPLETED: {
        // eslint-disable-next-line no-restricted-syntax
        const sourceIndex = _.findIndex(this.sources!, (source) => source.id === action.registryId);
        this.sources = reactUpdate(this.sources, {$splice: [[sourceIndex, 1]]});
        this.emitChange();
        break;
      }

      case EventConstants.FILE_BROWSER_SOURCES_FETCHED:
        {
          const {sources} = action;
          this.sources = sources;
          this.emitChange();
        }
        break;

      case EventConstants.FILE_BROWSER_SOURCE_UPDATED:
        {
          const {sourceId, sourceUpdates} = action;
          this._updateSource(sourceId, sourceUpdates);
          this.emitChange();
        }
        break;

      case EventConstants.STORABLES_MOVE_COMPLETED: {
        AppDispatcher.waitFor([StorableStore.dispatchIndex]);

        const {invalidatableStorableIds} = action;
        this._invalidateListedItems(invalidatableStorableIds);
        this.emitChange();
        break;
      }

      case EventConstants.BULK_ARCHIVE_PROCESS_MANAGEMENT:
      case EventConstants.BULK_ARCHIVE_BATCHES_COMPLETED:
      case EventConstants.BULK_ARCHIVE_CONNECTIONS:
      case EventConstants.BULK_ARCHIVE_FILES_COMPLETED:
      case EventConstants.BULK_ARCHIVE_ANALYSES_COMPLETED:
      case EventConstants.BULK_UNARCHIVE_BATCHES_COMPLETED:
      case EventConstants.BULK_UNARCHIVE_CONNECTIONS:
      case EventConstants.BULK_UNARCHIVE_FILES_COMPLETED:
      case EventConstants.BULK_UNARCHIVE_PROCESS_MANAGEMENT:
      case EventConstants.BULK_UNARCHIVE_ANALYSES_COMPLETED:
        {
          const {itemIds} = action;
          this._invalidateListedItems(itemIds);
        }
        break;

      case EventConstants.BULK_ARCHIVE_SOURCES_COMPLETED:
      case EventConstants.BULK_UNARCHIVE_SOURCES_COMPLETED:
        FileBrowserActions.fetchSources();
        break;

      case EventConstants.BULK_ARCHIVE_STORABLES_COMPLETED:
      case EventConstants.BULK_UNARCHIVE_STORABLES_COMPLETED:
        {
          const {itemIds} = action;
          this._invalidateListedItems(itemIds);
          this.emitChange();
        }
        break;

      case EventConstants.FILE_TAG_SCHEMA_UPDATED:
        {
          const {fileId} = action;
          this._invalidateListedItems([fileId]);
          this.emitChange();
        }
        break;

      // TODO(T6458): Change the event to FILE_TAGS_UPDATED
      case EventConstants.FILE_TAGS_WITH_FINDER_UPDATED:
        {
          const {tagData} = action;
          const itemIds = tagData.map(({id}: {id: string}) => id);
          this._invalidateListedItems(itemIds);
          this.emitChange();
        }
        break;

      case EventConstants.PROCESS_MANAGEMENT_ITEMS_UPDATED:
        {
          const {itemIds} = action;
          this._invalidateListedItems(itemIds);
          this.emitChange();
        }
        break;

      case EventConstants.FILE_BROWSER_UNREGISTER_FILES:
        {
          const {ids} = action;
          this._invalidateListedItems(ids);
          this.emitChange();
        }
        break;

      case EventConstants.SOURCES_UPDATED_FROM_GRAPHQL:
        {
          const {sources} = action;
          const presentSourcesById = _.indexBy(this.sources!, (source: NavStoreSource) => source.id);
          for (const source of sources) {
            if (presentSourcesById[source.id]) {
              this._updateSource(source.id, source);
            } else {
              // _updateSource accepts a Partial<NavStoreSource>, so we probably shouldn't change it to push
              // the source if not present.
              this.sources = reactUpdate(this.sources, {$push: [source]});
            }
          }
          this.emitChange();
        }
        break;

      default:
        break;
    }
    return true;
  }
}

export default new NavStore(AppDispatcher);
